// Selector state for the placement chrome.
//
// Resolution order, strongest first:
//   1. what the operator last picked for this platform + canvas shape
//      (localStorage)
//   2. the placement the ad itself was generated for (Ad.platformFormat)
//   3. the most common placement for the shape on that platform
//      (Meta 9:16 → Reels, Google 9:16 → YouTube Shorts, Meta feed
//      shapes → IG Feed, Google feed shapes → Discover)
//
// Keyed by platform + ratio rather than globally so flipping between a
// 9:16 and a 4:5 ad doesn't keep resetting the choice — and so picking
// Reels on a Meta ad doesn't then draw Reels furniture around a
// Performance Max ad that will never run there.
//
// An ad whose platformFormat is missing or unrecognised scopes to META,
// not to "everything" — see DEFAULT_SCOPE in chromeOptions.ts. Pass
// scope 'any' to opt into all platforms' placements (Title Studio).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  chromeOptionsForRatio,
  isChromeValidForRatio,
  readStoredChrome,
  resolveDefaultChrome,
  resolveScope,
  writeStoredChrome
} from './chromeOptions';
import type { AdRatio, ChromeKind, ChromeOption, ChromeScope } from './types';

export type UseAdChrome = {
  chrome:    ChromeKind;
  setChrome: (kind: ChromeKind) => void;
  options:   ChromeOption[];
  scope:     ChromeScope;
};

function resolve(ratio: AdRatio, platformFormat: string | null | undefined, scope: ChromeScope): ChromeKind {
  return readStoredChrome(ratio, scope) ?? resolveDefaultChrome(ratio, platformFormat, scope);
}

export function useAdChrome(
  ratio: AdRatio,
  platformFormat?: string | null,
  scopeOverride?: ChromeScope
): UseAdChrome {
  const scope = useMemo(
    () => resolveScope(platformFormat, scopeOverride),
    [platformFormat, scopeOverride]
  );
  const [chrome, setChromeState] = useState<ChromeKind>(() => resolve(ratio, platformFormat, scope));

  // Re-resolve when the previewed ad changes shape or placement. Guarded
  // on the resolved key so a parent re-render with the same ad doesn't
  // stomp a selection the operator just made.
  const lastKey = useRef(`${ratio}|${platformFormat || ''}|${scope}`);
  useEffect(() => {
    const key = `${ratio}|${platformFormat || ''}|${scope}`;
    if (key === lastKey.current) return;
    lastKey.current = key;
    setChromeState(resolve(ratio, platformFormat, scope));
  }, [ratio, platformFormat, scope]);

  const setChrome = useCallback((kind: ChromeKind) => {
    if (!isChromeValidForRatio(kind, ratio, scope)) return;
    setChromeState(kind);
    writeStoredChrome(ratio, kind, scope);
  }, [ratio, scope]);

  const options = useMemo(() => chromeOptionsForRatio(ratio, scope), [ratio, scope]);

  // Defensive: a stored value can go stale if the option table changes.
  const safeChrome = isChromeValidForRatio(chrome, ratio, scope)
    ? chrome
    : resolveDefaultChrome(ratio, platformFormat, scope);

  return { chrome: safeChrome, setChrome, options, scope };
}
